import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { FormGrid } from "@/components/ui/form-grid";
import { FormLabel } from "@/components/ui/form-label";
import { InputWrapper } from "@/components/ui/input-wrapper";
import { Surface } from "@/components/ui/surface";
import { Switch } from "@/components/ui/switch";
import { Text } from "@/components/ui/text";
import { VStack } from "@/components/ui/v-stack";
import { getTrailerTypes } from "@/lib/api/trailers";
import {
  createTrailerType,
  updateTrailerType,
  type TrailerTypeCatalogItem,
} from "@/lib/api/catalogs";
import {
  LocalizedNamesFields,
  toLocalizedName,
  type LocalizedNamesDraft,
} from "@/components/admin/catalogs/localized-names";
import { CatalogPageTemplate } from "@/components/admin/catalog-page-template";

type TrailerTypeRow = TrailerTypeCatalogItem & { is_deleted?: boolean };

function AdminTrailerTypesCatalogPage() {
  const { t } = useTranslation("admin.catalogs.trailerTypes");
  const { t: tCommon } = useTranslation("common");

  return (
    <CatalogPageTemplate<TrailerTypeRow>
      title={t("title")}
      tableId="catalog-trailer-types"
      queryKey={["admin", "catalogs", "trailer-types"]}
      fetchFn={
        getTrailerTypes as unknown as (params: {
          limit: number;
          offset: number;
        }) => Promise<{ data: TrailerTypeRow[]; total: number }>
      }
      columns={[
        {
          key: "name",
          header: t("columns.nameEn"),
          defaultWidth: 200,
          minWidth: 100,
          render: (item, isDeleted) => (
            <Text
              weight="medium"
              className={
                isDeleted
                  ? "text-[color:var(--on-surface-variant)] opacity-70"
                  : ""
              }
            >
              {item.names?.en ?? item.name}
            </Text>
          ),
        },
        {
          key: "tonnage",
          header: t("columns.tonnage"),
          defaultWidth: 100,
          minWidth: 60,
          render: (item, isDeleted) => (
            <Text
              className={
                isDeleted
                  ? "text-[color:var(--on-surface-variant)] opacity-70"
                  : ""
              }
            >
              {item.tonnage ?? "—"}
            </Text>
          ),
        },
        {
          key: "volume",
          header: t("columns.volume"),
          defaultWidth: 100,
          minWidth: 60,
          render: (item, isDeleted) => (
            <Text
              className={
                isDeleted
                  ? "text-[color:var(--on-surface-variant)] opacity-70"
                  : ""
              }
            >
              {item.volume ?? "—"}
            </Text>
          ),
        },
        {
          key: "refrigeration",
          header: t("columns.refrigeration"),
          defaultWidth: 140,
          minWidth: 80,
          render: (item, isDeleted) => (
            <Text
              className={
                isDeleted
                  ? "text-[color:var(--on-surface-variant)] opacity-70"
                  : ""
              }
            >
              {item.refrigeration_mode
                ? t("fields.refrigerationValueRefrigerated")
                : t("fields.refrigerationValueStandard")}
            </Text>
          ),
        },
      ]}
      searchFilter={(item, q) => {
        const names = [
          item.name,
          item.names?.en,
          item.names?.ru,
          item.names?.uz,
          item.names?.tr,
          item.names?.uz_cyr,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return names.includes(q);
      }}
      searchPlaceholder={t("searchPlaceholder")}
      emptyMessage={t("noItems")}
      addLabel={t("add")}
      editTitle={t("modalEditTitle")}
      addTitle={t("modalAddTitle")}
      failedToLoadMessage={t("failedToLoad")}
      failedToSaveMessage={t("failedToSave")}
      deleteEndpoint="/trailer-types"
      editLabel={t("edit")}
      itemToFormData={(item) => ({
        namesDraft: {
          en: item.names?.en ?? item.name ?? "",
          ru: item.names?.ru ?? "",
          uz: item.names?.uz ?? "",
        } as LocalizedNamesDraft,
        length: item.length ?? 0,
        width: item.width ?? 0,
        height: item.height ?? 0,
        pallets: item.pallets ?? 0,
        volume: item.volume ?? 0,
        tonnage: item.tonnage ?? 0,
        refrigeration_mode: Boolean(item.refrigeration_mode),
      })}
      emptyFormData={() => ({
        namesDraft: { en: "", ru: "", uz: "" } as LocalizedNamesDraft,
        length: 0,
        width: 0,
        height: 0,
        pallets: 0,
        volume: 0,
        tonnage: 0,
        refrigeration_mode: false,
      })}
      formDataToPayload={(fd) => ({
        name: (fd.namesDraft as LocalizedNamesDraft).en,
        names: toLocalizedName(fd.namesDraft as LocalizedNamesDraft),
        length: fd.length as number,
        width: fd.width as number,
        height: fd.height as number,
        pallets: fd.pallets as number,
        volume: fd.volume as number,
        tonnage: fd.tonnage as number,
        refrigeration_mode: fd.refrigeration_mode as boolean,
      })}
      isFormValid={(fd) =>
        Boolean((fd.namesDraft as LocalizedNamesDraft).en.trim())
      }
      createFn={(payload) =>
        createTrailerType(payload as Parameters<typeof createTrailerType>[0])
      }
      updateFn={(id, payload) =>
        updateTrailerType(
          id,
          payload as Parameters<typeof updateTrailerType>[1],
        )
      }
      renderForm={(formData, setFormData, submitting) => (
        <>
          <LocalizedNamesFields
            value={formData.namesDraft as LocalizedNamesDraft}
            onChange={(next) =>
              setFormData((prev) => ({ ...prev, namesDraft: next }))
            }
            disabled={submitting}
            testIdPrefix="admin-trailer-type"
          />

          <FormGrid>
            <VStack gap="xs">
              <FormLabel>{t("fields.length")}</FormLabel>
              <InputWrapper
                type="number"
                value={String(formData.length as number)}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    length: Number(e.target.value),
                  }))
                }
                disabled={submitting}
              />
            </VStack>
            <VStack gap="xs">
              <FormLabel>{t("fields.width")}</FormLabel>
              <InputWrapper
                type="number"
                value={String(formData.width as number)}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    width: Number(e.target.value),
                  }))
                }
                disabled={submitting}
              />
            </VStack>
            <VStack gap="xs">
              <FormLabel>{t("fields.height")}</FormLabel>
              <InputWrapper
                type="number"
                value={String(formData.height as number)}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    height: Number(e.target.value),
                  }))
                }
                disabled={submitting}
              />
            </VStack>
            <VStack gap="xs">
              <FormLabel>{t("fields.pallets")}</FormLabel>
              <InputWrapper
                type="number"
                value={String(formData.pallets as number)}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    pallets: Number(e.target.value),
                  }))
                }
                disabled={submitting}
              />
            </VStack>
            <VStack gap="xs">
              <FormLabel>{t("fields.volume")}</FormLabel>
              <InputWrapper
                type="number"
                value={String(formData.volume as number)}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    volume: Number(e.target.value),
                  }))
                }
                disabled={submitting}
              />
            </VStack>
            <VStack gap="xs">
              <FormLabel>{t("fields.tonnage")}</FormLabel>
              <InputWrapper
                type="number"
                value={String(formData.tonnage as number)}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    tonnage: Number(e.target.value),
                  }))
                }
                disabled={submitting}
              />
            </VStack>
          </FormGrid>

          <Surface
            border="default"
            padding="md"
            className="flex items-center justify-between gap-4"
          >
            <VStack gap="none">
              <Text weight="medium">{t("fields.refrigerationMode")}</Text>
              <Text size="xs" color="muted">
                {t("fields.refrigerationHelper")}
              </Text>
            </VStack>
            <Switch
              checked={formData.refrigeration_mode as boolean}
              onCheckedChange={(checked) =>
                setFormData((prev) => ({
                  ...prev,
                  refrigeration_mode: checked,
                }))
              }
              disabled={submitting}
            />
          </Surface>
        </>
      )}
      formSteps={(formData, setFormData, submitting) => [
        {
          title: tCommon("names"),
          content: (
            <LocalizedNamesFields
              value={formData.namesDraft as LocalizedNamesDraft}
              onChange={(next) =>
                setFormData((prev) => ({ ...prev, namesDraft: next }))
              }
              disabled={submitting}
              testIdPrefix="admin-trailer-type"
            />
          ),
        },
        {
          title: t("fields.specifications"),
          content: (
            <VStack gap="lg">
              <FormGrid cols="2">
                <VStack gap="xs">
                  <FormLabel>{t("fields.length")}</FormLabel>
                  <InputWrapper
                    type="number"
                    value={String(formData.length as number)}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        length: Number(e.target.value),
                      }))
                    }
                    disabled={submitting}
                  />
                </VStack>
                <VStack gap="xs">
                  <FormLabel>{t("fields.width")}</FormLabel>
                  <InputWrapper
                    type="number"
                    value={String(formData.width as number)}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        width: Number(e.target.value),
                      }))
                    }
                    disabled={submitting}
                  />
                </VStack>
                <VStack gap="xs">
                  <FormLabel>{t("fields.height")}</FormLabel>
                  <InputWrapper
                    type="number"
                    value={String(formData.height as number)}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        height: Number(e.target.value),
                      }))
                    }
                    disabled={submitting}
                  />
                </VStack>
                <VStack gap="xs">
                  <FormLabel>{t("fields.pallets")}</FormLabel>
                  <InputWrapper
                    type="number"
                    value={String(formData.pallets as number)}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        pallets: Number(e.target.value),
                      }))
                    }
                    disabled={submitting}
                  />
                </VStack>
                <VStack gap="xs">
                  <FormLabel>{t("fields.volume")}</FormLabel>
                  <InputWrapper
                    type="number"
                    value={String(formData.volume as number)}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        volume: Number(e.target.value),
                      }))
                    }
                    disabled={submitting}
                  />
                </VStack>
                <VStack gap="xs">
                  <FormLabel>{t("fields.tonnage")}</FormLabel>
                  <InputWrapper
                    type="number"
                    value={String(formData.tonnage as number)}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        tonnage: Number(e.target.value),
                      }))
                    }
                    disabled={submitting}
                  />
                </VStack>
              </FormGrid>

              <Surface
                border="default"
                padding="md"
                className="flex items-center justify-between gap-4"
              >
                <VStack gap="none">
                  <Text weight="medium">{t("fields.refrigerationMode")}</Text>
                  <Text size="xs" color="muted">
                    {t("fields.refrigerationHelper")}
                  </Text>
                </VStack>
                <Switch
                  checked={formData.refrigeration_mode as boolean}
                  onCheckedChange={(checked) =>
                    setFormData((prev) => ({
                      ...prev,
                      refrigeration_mode: checked,
                    }))
                  }
                  disabled={submitting}
                />
              </Surface>
            </VStack>
          ),
        },
      ]}
      testIdPrefix="admin-catalog-trailer-types"
    />
  );
}

export const Route = createFileRoute("/_app/admin/catalogs/trailer-types")({
  component: AdminTrailerTypesCatalogPage,
});
